import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { FiShoppingCart, FiCheck } from 'react-icons/fi';
import { gameAPI } from '../../services/api';
import { useTelegram } from '../../hooks/useTelegram';
import { useCart } from '../../context/CartContext';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Page = styled.div`
  min-height: 100vh;
  background: #000;
  padding-bottom: 160px;
  animation: ${fadeIn} 0.3s ease;
`;

const Hero = styled.div`
  height: 240px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #0a0a1a 100%);
`;

const HeroImg = styled.img`
  width: 140px;
  height: 140px;
  object-fit: contain;
  border-radius: 16px;
  filter: drop-shadow(0 20px 40px rgba(0,0,0,0.5));
`;

const HeroGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(transparent, #000);
`;

const Body = styled.div`
  padding: 0 16px;
  margin-top: -30px;
  position: relative;
  z-index: 3;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 4px;
  line-height: 1.2;
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: rgba(255,255,255,0.5);
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  margin: 16px 0 10px;
`;

const PriceCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${p => p.$selected ? 'rgba(0,122,255,0.15)' : 'rgba(255,255,255,0.04)'};
  border: 2px solid ${p => p.$selected ? '#007AFF' : 'transparent'};
  border-radius: 14px;
  padding: 12px 14px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.15s;
  &:active { transform: scale(0.98); }
`;

const Flag = styled.img`
  width: 44px;
  height: 32px;
  border-radius: 6px;
  flex-shrink: 0;
  object-fit: contain;
`;

const PriceInfo = styled.div`
  flex: 1;
`;

const RegionName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
`;

const RegionSub = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.4);
  margin-top: 1px;
`;

const Price = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #00d4ff;
`;

const BottomBar = styled.div`
  position: fixed;
  bottom: 70px;
  left: 0;
  right: 0;
  background: rgba(0,0,0,0.95);
  backdrop-filter: blur(20px);
  padding: 12px 16px;
  border-top: 1px solid rgba(255,255,255,0.08);
  z-index: 100;
`;

const BottomContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BottomPrice = styled.div`
  flex: 1;
`;

const PriceLabel = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.5);
`;

const PriceValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: #fff;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 10px;
`;

const CartButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${p => p.$inCart ? '#34c759' : '#007AFF'};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  padding: 14px 20px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  flex: ${p => p.$inCart ? '0 0 auto' : '1'};
  &:active { transform: scale(0.96); }
  &:disabled { opacity: 0.5; }
`;

const CheckoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #ff9500;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  padding: 14px 20px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  flex: 1;
  transition: all 0.15s;
  &:active { transform: scale(0.96); }
`;

const Description = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255,255,255,0.5);
  margin-top: 12px;
`;

const TIER_NAMES = {
  essential: 'PS Plus Essential',
  extra: 'PS Plus Extra',
  deluxe: 'PS Plus Deluxe',
  ea_play: 'EA Play',
};

const DURATION_NAMES = {
  '1m': '1 месяц',
  '3m': '3 месяца',
  '12m': '12 месяцев',
};

const REGION_CONFIG = {
  TR: { name: 'Турция', flag: '/icons/flag-tr.png?v=8' },
  UA: { name: 'Украина', flag: '/icons/flag-ua.png?v=8' },
  IN: { name: 'Индия', flag: '/icons/flag-in.png?v=8' },
  '🇹🇷': { name: 'Турция', flag: '/icons/flag-tr.png?v=8' },
  '🇺🇦': { name: 'Украина', flag: '/icons/flag-ua.png?v=8' },
  '🇮🇳': { name: 'Индия', flag: '/icons/flag-in.png?v=8' },
};

const TIER_DESCRIPTIONS = {
  essential: 'Онлайн-мультиплеер, 2-4 бесплатные игры каждый месяц, облачное хранилище и скидки.',
  extra: 'Всё из Essential + каталог 400+ игр для PS4/PS5.',
  deluxe: 'Всё из Extra + классические игры PS1/PS2/PSP и пробные версии.',
  ea_play: 'Библиотека игр EA: FIFA, Battlefield, NFS, The Sims. Скидки 10%.',
};

const PSPlusDetail = () => {
  const { tier, duration } = useParams();
  const navigate = useNavigate();
  const { tg, hapticFeedback } = useTelegram();
  const { addItem, removeItem, isInCart } = useCart();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    const goBack = () => navigate(-1);
    if (tg) {
      tg.setHeaderColor('#000000');
      tg.BackButton.show();
      tg.BackButton.onClick(goBack);
    }
    gameAPI.getPSPlus().then(data => {
      setItems(data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => {
      if (tg) {
        tg.BackButton.offClick(goBack);
        tg.BackButton.hide();
      }
    };
  }, [tg, navigate]);

  const formatPrice = (price) => {
    return price?.toLocaleString('ru-RU').replace(/\s/g, ' ');
  };

  // Filter items for this tier+duration, sort by price
  const regionItems = items
    .filter(i => i.tier === tier && i.duration === duration)
    .sort((a, b) => a.price_rub - b.price_rub);

  const selectedItem = regionItems[selectedIdx];
  const image = regionItems[0]?.image;
  const tierName = TIER_NAMES[tier] || tier;
  const durationName = DURATION_NAMES[duration] || duration;

  // Create cart item ID
  const getCartId = (item) => `psplus_${tier}_${duration}_${item.region || item.id}`;
  const inCart = selectedItem ? isInCart(getCartId(selectedItem)) : false;

  const handleCartClick = () => {
    if (!selectedItem) return;
    
    const regionCode = (selectedItem.region || 'TR').toLowerCase();
    const regionUpper = regionCode.toUpperCase();
    const regionName = REGION_CONFIG[regionUpper]?.name || regionCode;
    
    const cartItem = {
      id: getCartId(selectedItem),
      type: 'psplus',
      title: `${tierName} (${durationName})`,
      subtitle: regionName,
      image: selectedItem.image,
      image_url: selectedItem.image,
      // For Cart.js getPrice() function - region must be lowercase!
      selectedPrice: selectedItem.price_rub,
      selectedRegion: regionCode,
      region: regionCode,
      tier,
      duration,
    };

    if (inCart) {
      removeItem(getCartId(selectedItem));
      hapticFeedback?.('light');
    } else {
      addItem(cartItem);
      hapticFeedback?.('success');
    }
  };

  if (loading) {
    return (
      <Page>
        <Hero><div style={{ color: 'rgba(255,255,255,0.3)' }}>Загрузка...</div></Hero>
      </Page>
    );
  }

  return (
    <Page>
      <Hero>
        {image && <HeroImg src={image} alt={tierName} />}
        <HeroGradient />
      </Hero>

      <Body>
        <Title>{tierName}</Title>
        <Subtitle>{durationName}</Subtitle>

        <SectionTitle>Выберите регион</SectionTitle>
        
        {regionItems.map((item, idx) => {
          const regionCode = item.region || (idx === 0 ? 'TR' : idx === 1 ? 'UA' : 'IN');
          const region = REGION_CONFIG[regionCode] || { name: regionCode, flag: '🌍' };
          const isSelected = idx === selectedIdx;
          
          return (
            <PriceCard
              key={item.id || idx}
              $selected={isSelected}
              onClick={() => {
                setSelectedIdx(idx);
                hapticFeedback?.('light');
              }}
            >
              <Flag src={region.flag} alt={region.name} />
              <PriceInfo>
                <RegionName>{region.name}</RegionName>
                <RegionSub>PS Store {region.name}</RegionSub>
              </PriceInfo>
              <Price>{formatPrice(item.price_rub)} ₽</Price>
            </PriceCard>
          );
        })}

        <Description>
          {TIER_DESCRIPTIONS[tier] || 'Подписка PlayStation для вашего аккаунта.'}
        </Description>
      </Body>

      {selectedItem && (
        <BottomBar>
          <BottomContent>
            {!inCart && (
              <BottomPrice>
                <PriceLabel>{REGION_CONFIG[selectedItem.region]?.name || 'Регион'}</PriceLabel>
                <PriceValue>{formatPrice(selectedItem.price_rub)} ₽</PriceValue>
              </BottomPrice>
            )}
            <ButtonsRow style={{ flex: inCart ? 1 : 'none' }}>
              <CartButton $inCart={inCart} onClick={handleCartClick}>
                {inCart ? <FiCheck size={18} /> : <FiShoppingCart size={18} />}
                {inCart ? '✓' : 'В корзину'}
              </CartButton>
              {inCart && (
                <CheckoutButton onClick={() => { hapticFeedback?.('medium'); navigate('/cart'); }}>
                  К оформлению →
                </CheckoutButton>
              )}
            </ButtonsRow>
          </BottomContent>
        </BottomBar>
      )}
    </Page>
  );
};

export default PSPlusDetail;
